import Vue from 'vue'
import * as Sentry from '@sentry/browser'
import * as Integrations from '@sentry/integrations'
import { isInternetExplorer } from '~/util/featureDetect'
import { isExpectedAuthenticationFailure } from '~/utils/authenticationFailure'
import {
	sanitizeSentryBreadcrumb,
	sanitizeSentryEvent
} from '~/utils/sentrySanitization'

const BENIGN_RESIZE_OBSERVER_ERRORS = [
	'ResizeObserver loop completed with undelivered notifications.',
	'ResizeObserver loop limit exceeded'
]

export function filterExpectedAuthenticationFailure(event, hint) {
	return isExpectedAuthenticationFailure(hint?.originalException)
		? null
		: event
}

function textOf(value) {
	if (typeof value === 'string') return value
	if (value && typeof value.message === 'string') return value.message
	return ''
}

export function isBenignResizeObserverError(event, hint) {
	const texts = [
		textOf(hint?.originalException),
		textOf(event?.message),
		...(event?.exception?.values || []).map(entry => textOf(entry?.value))
	]

	return texts.some(text =>
		BENIGN_RESIZE_OBSERVER_ERRORS.some(message => text.includes(message))
	)
}

export function prepareSentryEvent(event, hint) {
	if (isBenignResizeObserverError(event, hint)) return null

	const filteredEvent = filterExpectedAuthenticationFailure(event, hint)

	return filteredEvent ? sanitizeSentryEvent(filteredEvent) : null
}

export default ({ $config }) => {
	// Skip Sentry initialization in IE
	if (isInternetExplorer()) {
		console.log('❌ Skipping Sentry: Internet Explorer detected')
		return
	}

	// Get DSN from runtime config (which comes from environment variables)
	const dsn = $config.sentryDsn
	const environment = $config.deployEnv || 'development'

	if (!dsn) {
		console.warn(
			'⚠️ Sentry DSN not configured for environment:',
			environment
		)
		return
	}

	try {
		Sentry.init({
			dsn,
			environment,
			integrations: [
				new Integrations.Vue({
					Vue,
					attachProps: true,
					logErrors: true
				})
			],
			release: process.env.npm_package_version || 'unknown',
			sampleRate: environment === 'staging' ? 0.1 : 1.0,
			debug: environment === 'development',
			beforeSend: prepareSentryEvent,
			beforeBreadcrumb: sanitizeSentryBreadcrumb
		})
	} catch (error) {
		console.error('❌ Sentry initialization failed:', error)
	}
}
